import { useContext, createContext, useMemo, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import candidatesServices, { GetAllStatusType } from '@/services/candidates';
import { ComponentWithChildrenType, FetchApiIds, ParsedAllStatusResponseType } from '@/types';
import { Paths } from '@/types/routes';
import { DEFAULT_STATUS_COLOR } from '@/utils/format';
import useApiFetch from './useApiFetch';

const defaultState = [];
const CandidateStatusesCtx = createContext<ParsedAllStatusResponseType[]>(defaultState);
const allowedPaths = [Paths.CANDIDATES, Paths.TALENT_POOL, Paths.HOME, Paths.JOBS, Paths.STAFF];

export function CandidateStatusesProvider({ children }: ComponentWithChildrenType): JSX.Element {
  const { pathname } = useLocation();
  const [cachedStatuses, setCachedStatuses] = useState<GetAllStatusType[]>([]);

  const isPathAllowed = useMemo(() => {
    const doesPathnameIncludesAllowedPath = (path: string): boolean => {
      return pathname.includes(path);
    };

    return allowedPaths.some(doesPathnameIncludesAllowedPath);
  }, [pathname]);

  const { data: allStatuses } = useApiFetch(
    [FetchApiIds.GET_CANDIDATE_STATUSES, pathname],
    candidatesServices.getAllStatus,
    {
      enabled: isPathAllowed,
      retry: 3,
      retryDelay: 1000,
    }
  );

  useEffect(() => {
    if (allStatuses) {
      setCachedStatuses(allStatuses);
    }
  }, [allStatuses, setCachedStatuses]);

  const filteredStatuses = useMemo(() => {
    return isPathAllowed ? cachedStatuses : [];
  }, [isPathAllowed, cachedStatuses]);

  const formatStatuses = useMemo(() => {
    const allStages = [...new Set(filteredStatuses.map(({ stage }) => stage))];

    return allStages.map((stage) => {
      const filterByStatusStage = (status: GetAllStatusType): boolean => status?.stage === stage;
      const returnOnlyStageStatus = (status: GetAllStatusType): string => status.status;

      const filteredByStatus = filteredStatuses
        .filter(filterByStatusStage)
        .map(returnOnlyStageStatus);
      const stageColor = filteredStatuses.find(filterByStatusStage)?.color;

      return {
        label: stage,
        options: filteredByStatus,
        color: stageColor ?? DEFAULT_STATUS_COLOR,
      };
    });
  }, [filteredStatuses]);

  return (
    <CandidateStatusesCtx.Provider value={formatStatuses}>{children}</CandidateStatusesCtx.Provider>
  );
}

export default function useCandidateStatuses(): ParsedAllStatusResponseType[] {
  return useContext(CandidateStatusesCtx);
}
